'use client';

import {
  createContext,
  forwardRef,
  useContext,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type TableHTMLAttributes,
  type ThHTMLAttributes,
  type TdHTMLAttributes,
} from 'react';
import { hasTypographyRole } from '../typography-role';

// Density is set on <Table> and threaded to cells: non-dense rows use py-3
// (12px — the V1 row rhythm, intentionally looser than the prior py-2), dense
// rows tighten to py-2. See V1-parity gap #1 (row-rhythm parity).
const TableDensityContext = createContext(false);

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  dense?: boolean;
}

export function Table({ dense = false, className, children, ...rest }: TableProps) {
  return (
    <TableDensityContext.Provider value={dense}>
      <table
        className={[
          'w-full border-collapse',
          // Exactly one body size role (#13496): non-dense 14px `text-body`, dense
          // 12px `text-body-sm`. The table used to emit `text-body-sm` unconditionally
          // as well, and it won in the compiled stylesheet, so every non-dense table
          // rendered at 12px. A caller role (e.g. `text-caption`) replaces it.
          hasTypographyRole(className) ? '' : dense ? 'text-body-sm' : 'text-body',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      >
        {children}
      </table>
    </TableDensityContext.Provider>
  );
}

export interface TableHeadProps extends HTMLAttributes<HTMLTableSectionElement> {
  /**
   * Pin the header row to the top of its scroll box -- the `<Table>` twin of
   * `DataTable stickyHeader` (ADR-0188). Pair it with a bounded
   * `TableScrollRegion` (`className="min-h-0 flex-1 overflow-auto
   * overscroll-none"`); without one, sticky resolves against the page. One
   * layer above --z-sticky, like DataTable's, so pinned body cells slide
   * under it. The collapsed row border does not travel with a sticky header,
   * so each header cell draws its own bottom rule as an inset shadow.
   */
  sticky?: boolean;
}

export function TableHead({ className, sticky = false, ...rest }: TableHeadProps) {
  return (
    <thead
      className={[
        'bg-surface-deep text-secondary text-body-sm',
        sticky &&
          'sticky top-0 z-[calc(var(--z-sticky)+1)] [&_th]:shadow-[inset_0_-1px_0_var(--color-border)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );
}
export function TableBody(props: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...props} />;
}
/**
 * Summary / totals footer. Recipe move #6 — a report table never floats without
 * a total. Set apart from the body by a heavier top rule + the elevated surface
 * tier + `font-medium`; cells inside use the normal `TableCell` so alignment +
 * density match the body columns.
 */
export function TableFooter({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tfoot
      className={[
        'border-t-2 border-border-strong bg-surface-elevated text-primary font-medium',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );
}
/**
 * Keyboard support (role="button", tabIndex=0, Enter/Space activation) is added
 * automatically whenever `onClick` is passed — do not nest an interactive
 * element (button/link/checkbox) inside a clickable row's cells, since that
 * creates ambiguous double-activation (row nav vs. the nested control) and a
 * broken a11y tree.
 *
 * forwardRef (#mega4-sample-tour): lets a consumer (e.g. catalog's item-list
 * sample-row anchor) grab the rendered <tr> DOM node to position a tour popover.
 */
export const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  function TableRow({ className, ...rest }, ref) {
    const { onClick, onKeyDown, role, tabIndex } = rest;
    const interactiveProps = onClick
      ? {
          role: role ?? 'button',
          tabIndex: tabIndex ?? 0,
          onKeyDown:
            onKeyDown ??
            ((event: KeyboardEvent<HTMLTableRowElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                if (event.key === ' ') event.preventDefault();
                onClick(event as unknown as MouseEvent<HTMLTableRowElement>);
              }
            }),
        }
      : undefined;
    return (
      <tr
        ref={ref}
        className={[
          'border-b border-border-subtle hover:bg-surface-sunken',
          onClick
            ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring-focus'
            : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...rest}
        {...interactiveProps}
      />
    );
  },
);
export function TableCell({ className, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  const dense = useContext(TableDensityContext);
  return (
    <td
      className={[`px-3 ${dense ? 'py-2' : 'py-3'} align-middle`, className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );
}
export function TableHeaderCell({ className, ...rest }: ThHTMLAttributes<HTMLTableCellElement>) {
  const dense = useContext(TableDensityContext);
  return (
    <th
      className={[
        `px-3 ${dense ? 'py-2' : 'py-3'} text-left text-table-header text-secondary uppercase`,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );
}
export function TableCaption({ className, ...rest }: HTMLAttributes<HTMLTableCaptionElement>) {
  return (
    <caption
      className={['mt-4 text-body-sm text-muted', className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
}
